// filepath: /Users/ephraim/yesodcopilot/client/src/components/group/CreateEventModal.js
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faTimes, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useGroup } from '../../context/GroupContext';
import groupService from '../../api/groupService';
import TimePicker from '../ui/time-picker';
import DatePicker from '../ui/date-picker';

const CreateEventModal = ({ 
  isOpen, 
  onClose, 
  groupId, 
  calendars = [],
  activeCalendar = null,
  calendarsLoading = false,
  selectedSlot = null,
  onEventCreated, 
  onEventUpdated, 
  event = null, 
  isEditMode = false,
  hideCalendarSelector = false // New prop to hide calendar selector in admin dashboard
}) => {
  const { adminGroup } = useGroup();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const modalRef = useRef(null);
  const [selectedCalendars, setSelectedCalendars] = useState(new Set(activeCalendar ? [activeCalendar] : []));
  const [isCalendarDropdownOpen, setIsCalendarDropdownOpen] = useState(false);
  const calendarDropdownRef = useRef(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [existingCategories, setExistingCategories] = useState([]);
  const [showCreateCategoryInline, setShowCreateCategoryInline] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const categoryDropdownRef = useRef(null);
  const [formData, setFormData] = useState({
    coverImage: null,
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // Auto-detect user's timezone
    repeat: 'none', // none, daily, weekly, monthly
    repeatDay: '', // for weekly repeat (Monday, Tuesday, etc.)
    allowSponsorship: false,
    sponsorshipAmount: '', // Changed from 0 to empty string
    paymentLinks: {
      paypal: '',
      zelle: '',
      cashapp: '',
      venmo: '',
      other: ''
    },
    requiresRsvp: false, // Registration required toggle (can be free or paid)
    requiresPayment: false, // Paid event toggle
    ticketPrice: '', // Ticket price in dollars (for flat pricing)
    pricingType: 'flat', // 'flat', 'tiered'
    pricingRules: [], // Array of pricing rules for tiered pricing
    category: '' // Event category/tag for filtering
  });
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [formStep, setFormStep] = useState(1);
  const [imageRemoved, setImageRemoved] = useState(false); // Track if image was explicitly removed
  const totalSteps = 2;

  // Initialize selectedCalendars when modal opens OR when activeCalendar becomes available
  useEffect(() => {
    if (activeCalendar) {
      setSelectedCalendars(new Set([activeCalendar]));
    }
  }, [activeCalendar]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarDropdownRef.current && !calendarDropdownRef.current.contains(event.target)) {
        setIsCalendarDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    if (isCalendarDropdownOpen || showCategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarDropdownOpen, showCategoryDropdown]);

  // Fetch existing categories when modal opens
  useEffect(() => {
    const fetchCategories = async () => {
      if (!isOpen || !groupId) return;
      
      try {
        const response = await groupService.getEventCategories(groupId);
        setExistingCategories(response.data || []);
      } catch (error) {
        console.error('Error fetching event categories:', error);
        // If endpoint doesn't exist yet, just use empty array
        setExistingCategories([]);
      }
    };

    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen, groupId]);

  // Initialize form data with event details if in edit mode
  useEffect(() => {
    if (isEditMode && event) {
      // Parse the UTC date without converting to local timezone
      // This ensures the time displayed matches what was originally entered
      const eventDate = new Date(event.date);
      const eventTimezone = event.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Convert UTC date to the event's timezone for display
      const dateInTimezone = new Date(eventDate.toLocaleString('en-US', { timeZone: eventTimezone }));
      const formattedDate = dateInTimezone.toISOString().split('T')[0]; // YYYY-MM-DD
      
      // Get hours and minutes in the event's timezone
      const hours = dateInTimezone.getHours().toString().padStart(2, '0');
      const minutes = dateInTimezone.getMinutes().toString().padStart(2, '0');
      const formattedTime = `${hours}:${minutes}`;

      setFormData({
        title: event.title || '',
        description: event.description || '',
        date: formattedDate,
        time: formattedTime,
        location: event.location || '',
        timeZone: eventTimezone,
        repeat: event.is_recurring ? (event.recurrence_pattern || 'none') : 'none',
        repeatDay: event.recurrence_day || '',
        allowSponsorship: event.allow_sponsorship || false,
        sponsorshipAmount: event.sponsorship_amount || '',
        paymentLinks: {
          paypal: event.payment_links?.paypal || '',
          zelle: event.payment_links?.zelle || '',
          cashapp: event.payment_links?.cashapp || '',
          venmo: event.payment_links?.venmo || '',
          other: event.payment_links?.other || ''
        },
        coverImage: null, // We can't upload the existing image directly, but we can show the preview
        requiresRsvp: event.requires_rsvp || false,
        requiresPayment: event.requires_payment || false,
        ticketPrice: event.ticket_price_cents ? (event.ticket_price_cents / 100).toFixed(2) : '',
        pricingType: event.pricing_type || 'flat',
        pricingRules: event.pricing_rules || [],
        category: event.category || ''
      });

      // Set cover image preview if available
      if (event.cover_image_url) {
        setCoverImagePreview(event.cover_image_url);
      }
    } else if (!isEditMode && selectedSlot) {
      // Prefill date and time from selected slot
      const startDate = new Date(selectedSlot.start);
      const formattedDate = startDate.toISOString().split('T')[0]; // YYYY-MM-DD
      const hours = startDate.getHours().toString().padStart(2, '0');
      const minutes = startDate.getMinutes().toString().padStart(2, '0');
      const formattedTime = `${hours}:${minutes}`;

      setFormData(prev => ({
        ...prev,
        date: formattedDate,
        time: formattedTime
      }));
    }
  }, [isEditMode, event, selectedSlot]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle payment link inputs
    if (name.startsWith('paymentLinks[')) {
      const linkType = name.match(/paymentLinks\[(\w+)\]/)[1];
      setFormData({
        ...formData,
        paymentLinks: {
          ...formData.paymentLinks,
          [linkType]: value
        }
      });
    } else if (name === 'sponsorshipAmount') {
      // Special handling for sponsorship amount to prevent floating-point issues
      setFormData({
        ...formData,
        [name]: value // Keep as string to preserve exact user input
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Handle calendar selection
  const handleCalendarToggle = (calendarId) => {
    setSelectedCalendars(prev => {
      const newSet = new Set(prev);
      if (newSet.has(calendarId)) {
        newSet.delete(calendarId);
      } else {
        newSet.add(calendarId);
      }
      return newSet;
    });
  };

  // Handle creating new category inline
  const handleCreateCategoryInline = () => {
    if (!newCategoryName.trim()) return;
    
    // Add to existing categories if not already there
    if (!existingCategories.includes(newCategoryName.trim())) {
      setExistingCategories([...existingCategories, newCategoryName.trim()]);
    }
    
    // Set as the selected category
    setFormData({
      ...formData,
      category: newCategoryName.trim()
    });
    
    // Reset and close
    setNewCategoryName('');
    setShowCreateCategoryInline(false);
    setShowCategoryDropdown(false);
  };

  // Handle clicking outside the modal
  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevent modal content clicks from bubbling up
  const handleModalContentClick = (e) => {
    e.stopPropagation();
  };

  // Handle cover image click with better event handling
  const handleCoverImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Cover image container clicked');
    const fileInput = document.getElementById('cover-image-input');
    if (fileInput) {
      console.log('File input found, triggering click');
      try {
        fileInput.click();
      } catch (error) {
        console.error('Error clicking file input:', error);
      }
    } else {
      console.error('File input not found');
    }
  };

  // Enhanced file change handler with better error handling
  const handleFileChange = (e) => {
    try {
      console.log('File input change event triggered');
      
      // Stop event propagation to prevent any bubbling
      e.stopPropagation();
      e.preventDefault();
      
      const file = e.target.files[0];
      
      if (!file) {
        console.log('No file selected');
        return;
      }

      console.log('File selected:', {
        name: file.name,
        type: file.type,
        size: `${Math.round(file.size / 1024)} KB`
      });
      
      // Clear any existing errors
      setError('');
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        console.error('File too large:', file.size);
        setError('Image size should be less than 5MB');
        return;
      }
      
      // Validate file type
      if (!file.type.match('image.*')) {
        console.error('Invalid file type:', file.type);
        setError('Please select an image file');
        return;
      }
      
      // Update form data with the file
      setFormData(prevData => ({
        ...prevData,
        coverImage: file
      }));
      
      // Reset the imageRemoved flag since a new image was selected
      setImageRemoved(false);
      
      // Create preview URL for the image
      const fileReader = new FileReader();
      
      fileReader.onload = (event) => {
        try {
          console.log('FileReader onload triggered');
          const result = event.target.result;
          setCoverImagePreview(result);
          console.log('Preview generated successfully');
        } catch (previewError) {
          console.error('Error setting preview:', previewError);
          setError('Failed to preview the image');
        }
      };
      
      fileReader.onerror = (error) => {
        console.error('FileReader error:', error);
        setError('Failed to read the image file');
      };
      
      // Start reading the file
      fileReader.readAsDataURL(file);
      
    } catch (error) {
      console.error('Error in handleFileChange:', error);
      setError('Failed to process the selected image');
    }
  };

  // Handler to remove cover image
  const handleRemoveImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Removing cover image');
    
    // Clear the form data and preview
    setFormData(prevData => ({
      ...prevData,
      coverImage: null
    }));
    setCoverImagePreview(null);
    setImageRemoved(true); // Mark that image was explicitly removed
    
    // Reset the file input
    const fileInput = document.getElementById('cover-image-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const resetForm = () => {
    setFormData({
      coverImage: null,
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      repeat: 'none',
      repeatDay: '',
      allowSponsorship: false,
      sponsorshipAmount: '', // Changed from 0 to empty string
      paymentLinks: {
        paypal: '',
        zelle: '',
        cashapp: '',
        venmo: '',
        other: ''
      },
      requiresPayment: false,
      ticketPrice: '',
      pricingType: 'flat',
      pricingRules: []
    });
    setCoverImagePreview(null);
    setImageRemoved(false); // Reset the image removed flag
    setFormStep(1);
    setError('');
  };

  // Enhanced form submission with better error handling
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsLoading(true);
    setError('');
    
    // Validate calendar selection
    if (selectedCalendars.size === 0) {
      setError('Please select at least one calendar');
      setIsLoading(false);
      return;
    }
    
    try {
      // Format date and time in the user's selected timezone
      const dateTimeString = `${formData.date}T${formData.time}:00`;
      const eventDateTime = new Date(dateTimeString);
      
      // Prepare the event data as JSON (not FormData)
      const eventData = {
        title: formData.title,
        description: formData.description,
        date: eventDateTime.toISOString(),
        timeZone: formData.timeZone, // Send timezone to backend
        location: formData.location,
        isRecurring: formData.repeat !== 'none',
        recurrencePattern: formData.repeat,
        allowSponsorship: formData.allowSponsorship,
        sponsorshipAmount: formData.sponsorshipAmount,
        paymentLinks: JSON.stringify(formData.paymentLinks), // Ensure consistent serialization
        calendarIds: Array.from(selectedCalendars), // Add selected calendars
        requiresRsvp: formData.requiresRsvp,
        requiresPayment: formData.requiresPayment,
        ticketPrice: formData.ticketPrice ? parseFloat(formData.ticketPrice) : null,
        pricingType: formData.pricingType,
        pricingRules: formData.pricingRules,
        category: formData.category || null
      };
      
      if (formData.repeat === 'weekly' && formData.repeatDay) {
        eventData.recurrenceDay = formData.repeatDay;
      }
      
      // Handle cover image - convert to base64 if present
      if (formData.coverImage && formData.coverImage instanceof File) {
        console.log('Converting image to base64:', {
          originalName: formData.coverImage.name,
          type: formData.coverImage.type,
          size: `${Math.round(formData.coverImage.size / 1024)} KB`
        });
        
        // Validate the file before processing
        if (formData.coverImage.size === 0) {
          throw new Error('Selected file is empty');
        }
        
        if (!formData.coverImage.type.startsWith('image/')) {
          throw new Error('Selected file is not a valid image');
        }
        
        // Convert file to base64
        const base64String = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(formData.coverImage);
        });
        
        eventData.coverImage = base64String;
        console.log('Image converted to base64 successfully');
      }

      let response;
      
      if (isEditMode && event) {
        // If image was explicitly removed, add the flag
        if (imageRemoved && !formData.coverImage) {
          eventData.removeCoverImage = true;
          console.log('Sending removeCoverImage flag to backend');
        }
        
        // Update existing event
        response = await groupService.updateEvent(groupId, event.id, eventData);
        console.log('Event updated successfully:', {
          id: response.data.id,
          title: response.data.title,
          coverImageUrl: response.data.cover_image_url
        });
        
        // Call the callback function to notify parent component
        if (onEventUpdated) {
          onEventUpdated(response.data);
        }
      } else {
        // Create new event
        response = await groupService.createEvent(groupId, eventData);
        console.log('Event created successfully:', {
          id: response.data.id,
          title: response.data.title,
          coverImageUrl: response.data.cover_image_url
        });
        
        // Call the callback function to notify parent component
        if (onEventCreated) {
          onEventCreated(response.data);
        }
      }
      
      resetForm();
      onClose();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} event:`, error);
      
      // Provide more specific error messages
      if (error.response && error.response.data && error.response.data.message) {
        setError(error.response.data.message);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError(`Failed to ${isEditMode ? 'update' : 'create'} event. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced step navigation with event handling
  const nextStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (formStep < totalSteps) {
      setFormStep(formStep + 1);
    }
  };

  const prevStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (formStep > 1) {
      setFormStep(formStep - 1);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm" onClick={handleOverlayClick}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col" ref={modalRef} onClick={handleModalContentClick}>
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          {formStep > 1 ? (
            <button 
              type="button"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" 
              onClick={prevStep}
            >
              <FontAwesomeIcon icon={faChevronLeft} /> Back
            </button>
          ) : (
            <div>{/* Empty div for spacing */}</div>
          )}
          <h2 className="text-xl font-semibold text-gray-900">{isEditMode ? 'Edit Event' : 'Create Event'}</h2>
          <button 
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        
        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <form onSubmit={handleSubmit} className="space-y-5">
            {formStep === 1 && (
              <div className="space-y-5">
                {/* Cover Image */}
                <div 
                  className={`relative w-full h-48 rounded-lg border-2 border-dashed cursor-pointer transition-all overflow-hidden ${
                    coverImagePreview 
                      ? 'border-transparent' 
                      : 'border-gray-300 bg-gray-50 hover:border-blue-500 hover:bg-blue-50'
                  }`}
                  onClick={handleCoverImageClick}
                  style={coverImagePreview ? {
                    backgroundImage: `url(${coverImagePreview})`, 
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  } : {}}
                >
                  <input 
                    type="file" 
                    id="cover-image-input" 
                    name="coverImage"
                    onChange={handleFileChange} 
                    accept="image/*"
                    className="hidden"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {!coverImagePreview && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                      <FontAwesomeIcon icon={faCamera} className="text-3xl mb-2" />
                      <span className="text-sm font-medium">Add cover image</span>
                    </div>
                  )}
                  {coverImagePreview && (
                    <>
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-all flex items-center justify-center">
                        <span className="text-white font-medium opacity-0 hover:opacity-100 transition-opacity">Change image</span>
                      </div>
                      <button
                        type="button"
                        className="absolute top-3 right-3 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                        onClick={handleRemoveImage}
                        title="Remove image"
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
                
                {/* Calendar Selection - Only show if hideCalendarSelector is false */}
                {!hideCalendarSelector && (
                  <div className="space-y-2" ref={calendarDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700">
                      Add to Calendar(s) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      {/* Dropdown Button */}
                      <button
                        type="button"
                        onClick={() => setIsCalendarDropdownOpen(!isCalendarDropdownOpen)}
                        disabled={calendarsLoading}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-left focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {calendarsLoading ? (
                            <span className="text-sm text-gray-500">Loading calendars...</span>
                          ) : selectedCalendars.size === 0 ? (
                            <span className="text-sm text-gray-400">Select calendar(s)</span>
                          ) : selectedCalendars.size === 1 ? (
                            <div className="flex items-center gap-2 min-w-0">
                              <div 
                                className="w-3 h-3 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: calendars.find(c => c.id === Array.from(selectedCalendars)[0])?.color || '#3b82f6' }}
                              />
                              <span className="text-sm text-gray-900 truncate">
                                {calendars.find(c => c.id === Array.from(selectedCalendars)[0])?.name}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-900">
                              {selectedCalendars.size} calendars selected
                            </span>
                          )}
                        </div>
                        <svg 
                          className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isCalendarDropdownOpen ? 'rotate-180' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Dropdown Menu */}
                      {isCalendarDropdownOpen && !calendarsLoading && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {calendars.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-gray-500">
                              No calendars available
                            </div>
                          ) : (
                            <div className="py-1">
                              {calendars.map(calendar => (
                                <label 
                                  key={calendar.id} 
                                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedCalendars.has(calendar.id)}
                                    onChange={() => handleCalendarToggle(calendar.id)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                                  />
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <div 
                                      className="w-3 h-3 rounded-full flex-shrink-0" 
                                      style={{ backgroundColor: calendar.color || '#3b82f6' }}
                                    />
                                    <span className="text-sm text-gray-900 truncate">{calendar.name}</span>
                                    {calendar.is_default && (
                                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex-shrink-0">
                                        Default
                                      </span>
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {selectedCalendars.size === 0 && !calendarsLoading && (
                      <p className="text-xs text-red-600 mt-1">Please select at least one calendar</p>
                    )}
                  </div>
                )}
                
                {/* Category */}
                <div className="space-y-2">
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                    Category <span className="text-xs text-gray-500 font-normal">(optional)</span>
                  </label>
                  <div className="space-y-2" style={{ position: 'relative' }} ref={categoryDropdownRef}>
                    <div 
                      className="tag-selector" 
                      onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                      style={{
                        cursor: 'pointer',
                        padding: '0.625rem 1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        backgroundColor: 'white',
                        minHeight: '42px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span style={{ color: formData.category ? '#111827' : '#9ca3af' }}>
                        {formData.category || 'Select or create a category'}
                      </span>
                      <FontAwesomeIcon 
                        icon={require('@fortawesome/free-solid-svg-icons').faChevronDown} 
                        style={{ fontSize: '0.75rem', color: '#6b7280' }}
                      />
                      
                      {showCategoryDropdown && (
                        <div className="tag-dropdown" style={{ width: '100%' }}>
                          <div className="tag-dropdown-options">
                            {/* None/Clear option */}
                            <div 
                              className={`dropdown-option ${!formData.category ? 'selected' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFormData({ ...formData, category: '' });
                                setShowCategoryDropdown(false);
                                setShowCreateCategoryInline(false);
                              }}
                            >
                              <span className="option-label" style={{ fontStyle: 'italic', color: '#6b7280' }}>
                                No category
                              </span>
                              {!formData.category && <span className="checkmark">✓</span>}
                            </div>
                            
                            {/* Existing categories */}
                            {existingCategories.map(category => (
                              <div 
                                key={category}
                                className={`dropdown-option ${formData.category === category ? 'selected' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFormData({ ...formData, category });
                                  setShowCategoryDropdown(false);
                                  setShowCreateCategoryInline(false);
                                }}
                              >
                                <span className="option-label">{category}</span>
                                {formData.category === category && <span className="checkmark">✓</span>}
                              </div>
                            ))}
                          </div>
                          
                          {/* Divider */}
                          <div className="dropdown-divider"></div>
                          
                          {/* Create Category Option */}
                          {!showCreateCategoryInline ? (
                            <div 
                              className="dropdown-option create-tag-option"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowCreateCategoryInline(true);
                              }}
                            >
                              <span className="option-label">+ Create New Category</span>
                            </div>
                          ) : (
                            <div className="create-tag-inline" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="text"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                placeholder="Category name..."
                                className="create-tag-input"
                                maxLength={100}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCreateCategoryInline();
                                  }
                                }}
                                autoFocus
                              />
                              <div className="create-tag-buttons">
                                <button
                                  type="button"
                                  className="create-tag-btn"
                                  onClick={handleCreateCategoryInline}
                                  disabled={!newCategoryName.trim()}
                                >
                                  ✓
                                </button>
                                <button
                                  type="button"
                                  className="cancel-tag-btn"
                                  onClick={() => {
                                    setShowCreateCategoryInline(false);
                                    setNewCategoryName('');
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      e.g., Holidays, Shabbat, Learning, Youth Program
                    </p>
                  </div>
                </div>
                
                {/* Title */}
                <div className="space-y-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input 
                    type="text" 
                    id="title" 
                    name="title" 
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400" 
                    placeholder="Give it a name"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                {/* Description */}
                <div className="space-y-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    About
                  </label>
                  <textarea 
                    id="description" 
                    name="description" 
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400 resize-none" 
                    placeholder="This is the description users will see"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    required
                  />
                </div>
                
                {/* Next Button */}
                <div className="flex justify-end pt-2">
                  <button 
                    type="button" 
                    className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
                    onClick={nextStep}
                    disabled={!formData.title || !formData.description || (!hideCalendarSelector && selectedCalendars.size === 0)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
            
            {formStep === 2 && (
              <div className="space-y-5">
                {/* Date */}
                <div className="space-y-2">
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                    Date
                  </label>
                  <DatePicker
                    value={formData.date ? new Date(formData.date) : null}
                    onChange={(date) => {
                      const dateString = date ? date.toISOString().split('T')[0] : '';
                      setFormData(prev => ({ ...prev, date: dateString }));
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900"
                    placeholder="Select date"
                    required
                  />
                </div>
                
                {/* Time */}
                <div className="space-y-2">
                  <label htmlFor="time" className="block text-sm font-medium text-gray-700">
                    Time
                  </label>
                  <TimePicker
                    value={formData.time}
                    onChange={(time) => setFormData(prev => ({ ...prev, time }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900"
                    required
                  />
                </div>

                {/* Timezone */}
                <div className="space-y-2">
                  <label htmlFor="timeZone" className="block text-sm font-medium text-gray-700">
                    Time Zone
                  </label>
                  <select
                    id="timeZone"
                    name="timeZone"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900"
                    value={formData.timeZone}
                    onChange={handleInputChange}
                  >
                    <option value="America/New_York">Eastern Time</option>
                    <option value="America/Chicago">Central Time</option>
                    <option value="America/Denver">Mountain Time</option>
                    <option value="America/Phoenix">Arizona Time</option>
                    <option value="America/Los_Angeles">Pacific Time</option>
                    <option value="America/Anchorage">Alaska Time</option>
                    <option value="Pacific/Honolulu">Hawaii Time</option>
                    <option value="Asia/Jerusalem">Israel Time</option>
                    <option value="Europe/London">UK Time</option>
                    <option value="Europe/Paris">Central European Time</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
                
                {/* Location */}
                <div className="space-y-2">
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                    Location
                  </label>
                  <input 
                    type="text" 
                    id="location" 
                    name="location" 
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400" 
                    placeholder="Add a location or link"
                    value={formData.location}
                    onChange={handleInputChange}
                  />
                </div>
                
                {/* Repeat */}
                <div className="space-y-2">
                  <label htmlFor="repeat" className="block text-sm font-medium text-gray-700">
                    Repeat
                  </label>
                  <select 
                    id="repeat" 
                    name="repeat" 
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900"
                    value={formData.repeat}
                    onChange={handleInputChange}
                  >
                    <option value="none">Does not repeat</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                  
                  {formData.repeat === 'weekly' && (
                    <div className="mt-3 space-y-2">
                      <label htmlFor="repeatDay" className="block text-sm font-medium text-gray-700">
                        Day of week
                      </label>
                      <select 
                        id="repeatDay" 
                        name="repeatDay" 
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900"
                        value={formData.repeatDay}
                        onChange={handleInputChange}
                      >
                        <option value="monday">Every Monday</option>
                        <option value="tuesday">Every Tuesday</option>
                        <option value="wednesday">Every Wednesday</option>
                        <option value="thursday">Every Thursday</option>
                        <option value="friday">Every Friday</option>
                        <option value="saturday">Every Saturday</option>
                        <option value="sunday">Every Sunday</option>
                      </select>
                    </div>
                  )}
                </div>
                
                {/* Allow Sponsorship Toggle - HIDDEN */}
                <div className="space-y-4 hidden">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Allow users to sponsor</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox"
                        id="allowSponsorship"
                        name="allowSponsorship"
                        checked={formData.allowSponsorship}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  
                  {/* Sponsorship Details */}
                  {formData.allowSponsorship && (
                    <div className="space-y-4 pl-4 border-l-2 border-blue-500">
                      {/* Sponsorship Amount */}
                      <div className="space-y-2">
                        <label htmlFor="sponsorshipAmount" className="block text-sm font-medium text-gray-700">
                          Sponsorship amount ($)
                        </label>
                        <input 
                          type="text"
                          id="sponsorshipAmount"
                          name="sponsorshipAmount"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                          value={formData.sponsorshipAmount}
                          onChange={handleInputChange}
                          placeholder="Enter amount (e.g., 12)"
                          pattern="[0-9]*\.?[0-9]*"
                          inputMode="decimal"
                        />
                      </div>
                      
                      {/* PayPal */}
                      <div className="space-y-2">
                        <label htmlFor="paypal" className="block text-sm font-medium text-gray-700">
                          PayPal link
                        </label>
                        <input 
                          type="url"
                          id="paypal"
                          name="paymentLinks[paypal]"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                          placeholder="Enter your PayPal link"
                          value={formData.paymentLinks.paypal}
                          onChange={handleInputChange}
                        />
                      </div>
                      
                      {/* Zelle */}
                      <div className="space-y-2">
                        <label htmlFor="zelle" className="block text-sm font-medium text-gray-700">
                          Zelle (email, phone, or link)
                        </label>
                        <input 
                          type="text"
                          id="zelle"
                          name="paymentLinks[zelle]"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                          placeholder="Enter Zelle email, phone number, or link"
                          value={formData.paymentLinks.zelle}
                          onChange={handleInputChange}
                        />
                      </div>
                      
                      {/* Venmo */}
                      <div className="space-y-2">
                        <label htmlFor="venmo" className="block text-sm font-medium text-gray-700">
                          Venmo (username, email, phone, or link)
                        </label>
                        <input 
                          type="text"
                          id="venmo"
                          name="paymentLinks[venmo]"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                          placeholder="Enter Venmo @username, email, phone, or link"
                          value={formData.paymentLinks.venmo}
                          onChange={handleInputChange}
                        />
                      </div>
                      
                      {/* Cash App */}
                      <div className="space-y-2">
                        <label htmlFor="cashapp" className="block text-sm font-medium text-gray-700">
                          Cash App (username, email, phone, or link)
                        </label>
                        <input 
                          type="text"
                          id="cashapp"
                          name="paymentLinks[cashapp]"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                          placeholder="Enter Cash App $username, email, phone, or link"
                          value={formData.paymentLinks.cashapp}
                          onChange={handleInputChange}
                        />
                      </div>
                      
                      {/* Other Payment Link */}
                      <div className="space-y-2">
                        <label htmlFor="other" className="block text-sm font-medium text-gray-700">
                          Other payment link
                        </label>
                        <input 
                          type="url"
                          id="other"
                          name="paymentLinks[other]"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                          placeholder="Enter other payment link"
                          value={formData.paymentLinks.other}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Registration Toggle */}
                <div className="space-y-4">
                  {/* Require Registration Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-700">Require Registration</span>
                      <span className="text-xs text-gray-500 mt-1">Users must RSVP to attend (can be free or paid)</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox"
                        id="requiresRsvp"
                        name="requiresRsvp"
                        checked={formData.requiresRsvp}
                        onChange={(e) => {
                          const isEnabled = e.target.checked;
                          // If disabling registration, also disable payment
                          if (!isEnabled) {
                            setFormData(prev => ({
                              ...prev,
                              requiresRsvp: false,
                              requiresPayment: false,
                              ticketPrice: '',
                              pricingType: 'flat',
                              pricingRules: []
                            }));
                          } else {
                            handleInputChange(e);
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {/* Require Payment Toggle - Only shown when registration is enabled */}
                  {formData.requiresRsvp && (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-700">Require Payment</span>
                        <span className="text-xs text-gray-500 mt-1">Users must pay to register</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox"
                          id="requiresPayment"
                          name="requiresPayment"
                          checked={formData.requiresPayment}
                          onChange={(e) => {
                            const isEnabled = e.target.checked;
                            // Check if Stripe is set up when enabling payment
                            if (isEnabled && (!adminGroup?.stripe_account_id || adminGroup?.onboarding_status !== 'active')) {
                              e.preventDefault();
                              setError('Please complete your payment setup first. Go to the Financials page to connect Stripe.');
                              return;
                            }
                            // Clear all pricing data when disabling payment
                            if (!isEnabled) {
                              setFormData(prev => ({
                                ...prev,
                                requiresPayment: false,
                                ticketPrice: '',
                                pricingType: 'flat',
                                pricingRules: []
                              }));
                            } else {
                              handleInputChange(e);
                            }
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  )}
                  
                  {/* Ticket Pricing Configuration */}
                  {formData.requiresPayment && (
                    <div className="space-y-4 pl-4 border-l-2 border-blue-500">
                      {/* Pricing Type Selector */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Pricing Structure
                        </label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                pricingType: 'flat',
                                pricingRules: []
                              }));
                            }}
                            className={`flex-1 px-4 py-2.5 rounded-lg border-2 font-medium transition-all ${
                              formData.pricingType === 'flat'
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            Fixed Price
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                pricingType: 'tiered',
                                ticketPrice: '',
                                pricingRules: prev.pricingRules.length === 0 ? [
                                  { userType: '', label: '', priceCents: '' }
                                ] : prev.pricingRules
                              }));
                            }}
                            className={`flex-1 px-4 py-2.5 rounded-lg border-2 font-medium transition-all ${
                              formData.pricingType === 'tiered'
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            Multiple Prices
                          </button>
                        </div>
                        <p className="text-xs text-gray-500">
                          {formData.pricingType === 'flat' 
                            ? 'Same price for everyone' 
                            : 'Different prices for different user types (member/non-member, adult/child, etc.)'}
                        </p>
                      </div>

                      {/* Flat Pricing Input */}
                      {formData.pricingType === 'flat' && (
                        <div className="space-y-2">
                          <label htmlFor="ticketPrice" className="block text-sm font-medium text-gray-700">
                            Ticket Price ($)
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-sm">$</span>
                            </div>
                            <input 
                              type="text"
                              id="ticketPrice"
                              name="ticketPrice"
                              className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                              value={formData.ticketPrice}
                              onChange={handleInputChange}
                              placeholder="0.00"
                              pattern="[0-9]*\.?[0-9]*"
                              inputMode="decimal"
                              required={formData.requiresPayment && formData.pricingType === 'flat'}
                            />
                          </div>
                          <p className="text-xs text-gray-500">
                            Leave empty or enter 0 for free registration
                          </p>
                        </div>
                      )}

                      {/* Tiered Pricing Configuration */}
                      {formData.pricingType === 'tiered' && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">
                              Price Options
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  pricingRules: [
                                    ...prev.pricingRules,
                                    { userType: '', label: '', priceCents: '' }
                                  ]
                                }));
                              }}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              + Add Price Option
                            </button>
                          </div>

                          {/* Quick Templates */}
                          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                            <p className="text-xs font-medium text-gray-700">Quick Templates:</p>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    pricingRules: [
                                      { userType: 'member', label: 'Member', priceCents: '0' },
                                      { userType: 'non_member', label: 'Non-Member', priceCents: '1000' }
                                    ]
                                  }));
                                }}
                                className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                              >
                                Member/Non-Member
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    pricingRules: [
                                      { userType: 'child', label: 'Child (under 12)', priceCents: '300' },
                                      { userType: 'adult', label: 'Adult', priceCents: '1000' }
                                    ]
                                  }));
                                }}
                                className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                              >
                                Child/Adult
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    pricingRules: [
                                      { userType: 'student', label: 'Student', priceCents: '500' },
                                      { userType: 'senior', label: 'Senior (65+)', priceCents: '700' },
                                      { userType: 'general', label: 'General Admission', priceCents: '1000' }
                                    ]
                                  }));
                                }}
                                className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                              >
                                Student/Senior/General
                              </button>
                            </div>
                          </div>

                          {/* Pricing Rules */}
                          <div className="space-y-3">
                            {formData.pricingRules.map((rule, index) => (
                              <div key={index} className="bg-white border border-gray-300 rounded-lg p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-700">Price Option {index + 1}</span>
                                  {formData.pricingRules.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setFormData(prev => ({
                                          ...prev,
                                          pricingRules: prev.pricingRules.filter((_, i) => i !== index)
                                        }));
                                      }}
                                      className="text-red-600 hover:text-red-700 text-sm"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Type
                                    </label>
                                    <input
                                      type="text"
                                      value={rule.label}
                                      onChange={(e) => {
                                        const newRules = [...formData.pricingRules];
                                        newRules[index].label = e.target.value;
                                        // Auto-generate userType from label (lowercase, replace spaces with underscores)
                                        newRules[index].userType = e.target.value.toLowerCase().replace(/\s+/g, '_');
                                        setFormData(prev => ({ ...prev, pricingRules: newRules }));
                                      }}
                                      placeholder="e.g., Member, Child, Student"
                                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      required
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Price ($)
                                    </label>
                                    <div className="relative">
                                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-500 text-sm">$</span>
                                      </div>
                                      <input
                                        type="text"
                                        value={rule.priceCents ? (parseFloat(rule.priceCents) / 100).toFixed(2) : ''}
                                        onChange={(e) => {
                                          const dollars = e.target.value;
                                          const cents = dollars ? Math.round(parseFloat(dollars) * 100).toString() : '';
                                          const newRules = [...formData.pricingRules];
                                          newRules[index].priceCents = cents;
                                          setFormData(prev => ({ ...prev, pricingRules: newRules }));
                                        }}
                                        placeholder="0.00"
                                        pattern="[0-9]*\.?[0-9]*"
                                        inputMode="decimal"
                                        className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                      />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Enter 0 for free
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {formData.pricingRules.length === 0 && (
                            <div className="text-center py-6 text-gray-500 text-sm">
                              Click "Add Price Option" or use a quick template to get started
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Submit Button */}
                <div className="flex justify-end pt-2">
                  <button 
                    type="submit" 
                    className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" 
                    disabled={isLoading || !formData.date || !formData.time}
                  >
                    {isLoading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Event' : 'Create Event')}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEventModal;